// Package imports:
import { ChangeEvent, ForwardedRef, forwardRef, memo, MouseEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import cx from 'classnames'

// Define the base props that extend from standard HTML input attributes
type CheckProps = React.InputHTMLAttributes<HTMLInputElement>;

// Define custom props specific to this checkbox component
interface IOwnProps {
    label?: string | JSX.Element,
    labelComponent?: JSX.Element
}

// Combine the base input props with our custom props
type Props = CheckProps & IOwnProps;

const Checkbox = forwardRef(({
    id,
    label,
    className = '',
    value,
    onChange = () => { },
    labelComponent,
    name,
    ...props
}: Props,
    ref: ForwardedRef<any>
) => {
    // Handle the change event when the checkbox is toggled
    // Convert the checked boolean to a string value for consistency
    const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
        event.target.value = event.target.checked.toString();
        onChange(event);
    }

    // Handle mousedown event on the label to trigger the checkbox
    // This allows the checkbox to respond even when the mouse is moving
    const handleMouseDown = (event: MouseEvent<HTMLLabelElement>) => {
        // Prevent the default behavior to avoid any unwanted interactions
        event.preventDefault();

        // Only proceed if the checkbox is not disabled
        if (!props.disabled) {
            // Get the input element via the htmlFor attribute
            const input = document.getElementById(id || '') as HTMLInputElement;
            if (input) {
                // Toggle the checked state
                input.checked = !input.checked;

                // Create a synthetic change event to trigger onChange handler
                const syntheticEvent = {
                    target: {
                        ...input,
                        value: input.checked.toString(),
                        checked: input.checked
                    }
                } as ChangeEvent<HTMLInputElement>;

                // Call the onChange handler with the synthetic event
                onChange(syntheticEvent);
            }
        }
    }

    return (
        <div
            className={cx('KCL_checkbox', className, {
                'is-disabled': props.disabled,
                'checked': value === 'true'
            })}
        >
            {/* Hidden checkbox input that maintains the actual form state */}
            <input
                ref={ref}
                id={id}
                type="checkbox"
                name={name}
                checked={value === 'true'}
                onChange={handleOnChange}
                {...props}
            />

            {/* Label that acts as the visible clickable area */}
            {/* Using onMouseDown instead of onClick to capture interaction even when mouse is moving */}
            <label
                htmlFor={id}
                className="checkbox__label"
                onMouseDown={handleMouseDown}
            >
                {/* Visual checkbox icon container */}
                <span className="checkbox__icon">
                    <i>
                        <FontAwesomeIcon icon={faCheck} />
                    </i>
                </span>

                {/* Display text label if provided */}
                {label && (
                    <span>{label}</span>
                )}

                {/* Display custom label component if provided */}
                {labelComponent && (
                    <span>{labelComponent}</span>
                )}
            </label>
        </div>
    );
});

export default memo(Checkbox);